import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import AssetsCombobox, { AssetsProvider } from '@/components/ui/AssetsCombobox';
import { DataRetentionSchedule } from '@/js/external-js/variableModules/namespaces/DataRetentionSchedule.namespace';
import { DataRetentionRecordUI, toUI, fromUI } from '../dataTypeMapping';
import { syncAssetsDataTypesShared } from '../assetDataTypeSyncUtil';
import { MCMMappingsCard, type MappingLink } from '@/components/mcm/MCMMappingsCard';
import { useTenancy } from '@/contexts/TenancyContext';

type DataTypeEntity = DataRetentionSchedule.DataTypeEntity;

interface DataRetentionDialogProps {
  open: boolean;
  record: DataTypeEntity | null;
  suppliers: string[];
  assets: string[];
  onClose: () => void;
  onSave: (updated: DataTypeEntity, linkedSuppliers: string[], linkedAssets: string[]) => void;
}

export const DataRetentionDialog: React.FC<DataRetentionDialogProps> = ({
  open,
  record,
  suppliers,
  assets,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<DataRetentionRecordUI | null>(record ? toUI(record) : null);
  const [linkedAssets, setLinkedAssets] = useState<string[]>([]);
  const prevLinkedAssets = useRef<string[]>([]);
  const { activeTenancy } = useTenancy();
  const [controlIds, setControlIds] = useState<string[]>([]);

  const defaultEntityLink = React.useMemo<MappingLink | undefined>(() => {
    if (!form) return undefined;
    return {
      id: `internal:datatype:${String(form.dataTypeID)}`,
      ref: String(form.dataTypeID),
      title: form.dataTypeName || 'Data Record',
      domain: 'Data',
      relation: 'relates-to',
    };
  }, [form?.dataTypeID, form?.dataTypeName]);

  // Sync dataTypesShared on assets when linkedAssets changes
  useEffect(() => {
    if (!form || typeof form.dataTypeID === 'undefined' || form.dataTypeID === null) return;
    const prev = prevLinkedAssets.current;
    const added = linkedAssets.filter(id => !prev.includes(id));
    const removed = prev.filter(id => !linkedAssets.includes(id));
    if (added.length > 0) {
      syncAssetsDataTypesShared(added, form.dataTypeID, true);
    }
    if (removed.length > 0) {
      syncAssetsDataTypesShared(removed, form.dataTypeID, false);
    }
    prevLinkedAssets.current = linkedAssets;
  }, [linkedAssets, form?.dataTypeID]);

  React.useEffect(() => {
    async function resolveAssetIds() {
      if (record) {
        const ui = toUI(record);
        setForm(ui);
        // Convert asset names in props.assets to IDs for combobox
        const { getAssets } = await import('@/services/assetsService');
        const allAssets = await getAssets();

        const nameToId = new Map(allAssets.map(a => [a.assetDesc, a.assetID]));
        const ids = (ui.assets ?? []).map(name => {
          const id = nameToId.get(name);
          if (typeof id === 'number' || typeof id === 'string') {
            return id.toString(); // always use assetID as string, even if 0
          }
          return name; // fallback if asset is not in the asset list
        }).filter(Boolean);
        console.log({nameToId: Array.from(nameToId.entries()), uiAssets: ui.assets, mappedIds: ids});
        setLinkedAssets(ids);
        prevLinkedAssets.current = ids;
      } else {
        setForm(null);
        setLinkedAssets([]);
        prevLinkedAssets.current = [];
      }
    }
    resolveAssetIds();
  }, [record, assets]);

  if (!form) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    let newValue: any = value;
    if (target.type === 'checkbox') {
      newValue = (target as HTMLInputElement).checked;
    } else if (name === 'dataRetentionPeriodMin' || name === 'dataRetentionPeriodMax') {
      newValue = Number(value);
    }
    setForm(prev => prev ? {
      ...prev,
      [name]: newValue
    } : null);
  };

  const handleSave = () => {
    if (form) {
      // Treat assets as the single source of truth for both assets and suppliers
      // Pass linkedAssets as IDs
      const entity = fromUI({ ...form, assets: linkedAssets, suppliers: linkedAssets });
      onSave(entity, linkedAssets, linkedAssets);
    }
  };


  return (
    <Dialog open={open} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">View / Edit Data Record</DialogTitle>
          </div>
        <div className="flex flex-col gap-4 px-4 pb-4">
          <Label htmlFor="dataTypeName">Type</Label>
          <Input id="dataTypeName" name="dataTypeName" value={form.dataTypeName} onChange={handleChange} />

          <Label htmlFor="dataTypeJustification">Description</Label>
          <Input id="dataTypeJustification" name="dataTypeJustification" value={form.dataTypeJustification} onChange={handleChange} />

          <div className="flex flex-col gap-2">
            <Label>Retention Period</Label>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium">{form.dataRetentionPeriodUnit === 'minutes' ? Math.round(form.dataRetentionPeriodMin) : form.dataRetentionPeriodMin} {form.dataRetentionPeriodUnit}</span>
              <span className="mx-1">–</span>
              <span className="text-sm font-medium">{form.dataRetentionPeriodUnit === 'minutes' ? Math.round(form.dataRetentionPeriodMax) : form.dataRetentionPeriodMax} {form.dataRetentionPeriodUnit}</span>
              <Select
                value={form.dataRetentionPeriodUnit}
                onValueChange={unit => setForm(f => f ? { ...f, dataRetentionPeriodUnit: unit } : f)}
              >
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="weeks">Weeks</SelectItem>
                  <SelectItem value="months">Months</SelectItem>
                  <SelectItem value="years">Years</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Slider
              min={0}
              max={100}
              step={1}
              value={[form.dataRetentionPeriodMin, form.dataRetentionPeriodMax]}
              onValueChange={([min, max]) => setForm(f => f ? { ...f, dataRetentionPeriodMin: min, dataRetentionPeriodMax: max } : f)}
              className="w-full"
            />
          </div>

          <Label htmlFor="dataTypeModeOfCollection">Legal Basis</Label>
          <Input id="dataTypeModeOfCollection" name="dataTypeModeOfCollection" value={form.dataTypeModeOfCollection} onChange={handleChange} />

          <div className="flex gap-2 items-center">
            <Label htmlFor="isPersonalInformation">Personal Information?</Label>
            <Checkbox
              id="isPersonalInformation"
              name="isPersonalInformation"
              checked={!!form.isPersonalInformation}
              onCheckedChange={checked => handleChange({ target: { name: 'isPersonalInformation', checked, type: 'checkbox' } } as any)}
            />
          </div>

          <Label htmlFor="status">Status</Label>
          <Select value={form.status} onValueChange={val => setForm(prev => prev ? { ...prev, status: val as 'Active' | 'Inactive' } : prev)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Label>Linked Assets & Suppliers</Label>
          <AssetsProvider>
            <AssetsCombobox
              selected={linkedAssets}
              setSelected={setLinkedAssets}
              placeholder="Select assets or suppliers..."
              fullWidth
            />
          </AssetsProvider>
        </div>
        <div className="px-4 pb-4">
        <MCMMappingsCard
          tenancyId={activeTenancy?.id}
          isReadOnly={false}
          controlIds={controlIds}
          defaultEntityLink={defaultEntityLink}
          onEnsureControlLinked={(controlId) => setControlIds((prev) => Array.from(new Set([...(prev || []), String(controlId)])))}
          collapsible
        />
        </div>
        <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
